import { CrudStoreItemOnboarding } from 'legacy/lut/classes/models/onboarding/CrudStoreItemOnboarding'
import { INotificationTemplate } from 'legacy/lut/classes/models/onboarding/INotificationTemplate'
import { ERequestTemplateNotificationType } from 'legacy/lut/classes/models/onboarding/RequestTemplate'
import { ActionNotifMode } from 'legacy/lut/components/widgets/public/onboarding/templates/actionDetail/ActionNotificationDetail'
import { getErrorResponse } from 'legacy/lut/functions'

import {
  IDataProviderFilter,
  IDataProviderSort,
  IFilter,
  IPaging,
  IPagingFromTo,
  Logic,
  Operator,
} from 'core-components/types'
import WebSocketStore from 'core/classes/services/WebSocketStore'
import { CrudStore } from 'core/types/crudStore'
import Logger from 'core/utils/Logger'
import { AlreadyExistsError } from 'onboarding/classes/errors/AlreadyExistsError'
import { ValidationParamsError } from 'onboarding/classes/errors/ValidationParamsError'
import {
  TCreateNotificationTemplatePayload,
  TNotificationTemplateDB,
  TUpdateNotificationTemplatePayload,
} from 'onboarding/classes/models/notificationTemplate'
import { IValidationParamsErrorPayload } from 'onboarding/types/request'
import { IInvalidParamsErrorResponsePayload } from 'onboarding/types/response'
import {
  throwOnErrorWithoutPayload,
  throwOnInvalidRequestParams,
} from 'onboarding/utils/errorResponse/throwOnApiError'
import { restCall, restErrorBody, restErrorStatus } from 'shared/lib/http/restCall'
import settings from 'src/config/settings'

const WS_CONTROLLER = 'notification-template'

const NOTIFICATION_TEMPLATES_ENDPOINT = `${settings.httpsServices.onboarding}/api/v1/onboarding/notification-templates`

interface IOnboardingProblem {
  code?: string
  title?: string
  detail?: string
  existingId?: number | string
  errors?: Array<{ field?: string; message?: string; code?: string }>
}

// Maps the REST endpoint's RFC 9457 problem+json into the same error classes the
// create/update callers already handle (AlreadyExistsError → offer the existing
// template, ValidationParamsError → field errors, else a plain Error), so the UI
// error UX is unchanged by the WS→REST switch.
function toWriteError(e: unknown, fallbackMessage: string): Error {
  const problem = restErrorBody<IOnboardingProblem>(e)

  if (problem) {
    const message = problem.detail || problem.title || fallbackMessage

    if (problem.code === 'CONFLICT') {
      return new AlreadyExistsError(message, problem.existingId)
    }

    if (problem.code === 'VALIDATION_FAILED') {
      const validatorResult: IValidationParamsErrorPayload[] = (problem.errors ?? []).map(
        (fieldError) => ({
          field: fieldError.field ?? '',
          message: fieldError.message ?? 'Invalid value',
          type: fieldError.code ?? 'invalid',
          actual: '',
          expected: '',
        })
      )
      return new ValidationParamsError(message, validatorResult)
    }

    return new Error(message)
  }

  return e instanceof Error ? e : new Error(fallbackMessage)
}

class NotificationTemplateApi {
  public getActiveUniqueNamesCrud(excludeUUIDs?: string[]) {
    const crud = CrudStore.get(CrudStoreItemOnboarding.NotificationTemplatesUniqList)

    if (excludeUUIDs && excludeUUIDs.length) {
      const filters = (crud.getFilter()?.filters || []) as IFilter[]
      crud.setFilter({
        logic: Logic.AND,
        filters: [
          ...filters.filter((el) => el.field != 'ExcludeUUIDs'),
          {
            field: 'ExcludeUUIDs',
            operator: Operator.NOTEQUAL,
            value: excludeUUIDs,
          },
        ],
      })
    }
    return crud
  }

  public getActiveWrappersCrud() {
    // REST-backed CRUD (GET /api/v1/onboarding/notification-templates/wrappers);
    // the active + wrapper filters are applied server-side.
    return CrudStore.get(CrudStoreItemOnboarding.NotificationTemplatesWrappers)
  }

  // Updates a template via the onboarding REST endpoint
  // (PUT /api/v1/onboarding/notification-templates/{uuid}), which returns the
  // updated template as a plain JSON body and RFC 9457 problem+json on error.
  public async updateOne(
    uuid: string,
    payload: TUpdateNotificationTemplatePayload
  ): Promise<TNotificationTemplateDB> {
    try {
      return await restCall<TNotificationTemplateDB>({
        method: 'put',
        url: `${NOTIFICATION_TEMPLATES_ENDPOINT}/${encodeURIComponent(uuid)}`,
        payload,
      })
    } catch (e: unknown) {
      throw toWriteError(e, 'Failed')
    }
  }

  // Creates a template via the onboarding REST endpoint
  // (POST /api/v1/onboarding/notification-templates), which returns the created
  // template as a plain JSON body and RFC 9457 problem+json on error.
  public async createActiveOne(
    payload: TCreateNotificationTemplatePayload
  ): Promise<TNotificationTemplateDB> {
    try {
      return await restCall<TNotificationTemplateDB>({
        method: 'post',
        url: NOTIFICATION_TEMPLATES_ENDPOINT,
        payload,
      })
    } catch (e: unknown) {
      throw toWriteError(e, 'Failed')
    }
  }

  // Fetches one full template from the onboarding REST endpoint
  // (GET /api/v1/onboarding/notification-templates/{id}); {id} is the numeric
  // EntryId the admin UI carries in its URLs (the endpoint also accepts a UUID).
  public async getById(id: number): Promise<INotificationTemplate | null> {
    try {
      return await restCall<INotificationTemplate>({
        method: 'get',
        url: `${NOTIFICATION_TEMPLATES_ENDPOINT}/${id}`,
      })
    } catch (e: unknown) {
      if (restErrorStatus(e) === 404) {
        return null
      }
      throw e
    }
  }

  // All active locale variants of a template, from the onboarding REST endpoint
  // (GET /api/v1/onboarding/notification-templates/locales?templateName=…). The
  // caller passes only the name; the active filter is applied server-side. Full
  // records, so switching locales fills the editor without a second fetch.
  public async fetchActiveLocales(templateName: string): Promise<INotificationTemplate[]> {
    return restCall<INotificationTemplate[]>({
      method: 'get',
      url: `${NOTIFICATION_TEMPLATES_ENDPOINT}/locales?templateName=${encodeURIComponent(
        templateName
      )}`,
    })
  }

  public async fetchLocaleOptions(): Promise<TNotificationLocaleOption[]> {
    return restCall<TNotificationLocaleOption[]>({
      method: 'get',
      url: `${NOTIFICATION_TEMPLATES_ENDPOINT}/locale-options`,
    })
  }

  // Reads the unique-name notification-template list from the onboarding REST
  // endpoint (GET /api/v1/onboarding/notification-templates), which returns a
  // plain JSON array. Backs the NotificationTemplatesUniqList CRUD provider.
  public async fetchMany(
    filter: IDataProviderFilter,
    paging: IPaging | IPagingFromTo,
    sort: IDataProviderSort | IDataProviderSort[] | undefined
  ) {
    const { page, pageSize } = paging as IPaging
    const { field, dir } = sort as IDataProviderSort

    const filters = filter.filters as IFilter[]
    const inputSearch = filters.find((el) => el.field === 'InputSearch')?.value as
      | string
      | undefined
    const subject = filters.find((el) => el.field === 'Subject')?.value as string | undefined
    const excludeUUIDs = filters.find((el) => el.field === 'ExcludeUUIDs')?.value as
      | string[]
      | undefined

    // page is 0-based in the CRUD store; the endpoint expects 1-based.
    const params = new URLSearchParams()
    params.set('page', String(page + 1))
    params.set('itemsPerPage', String(pageSize))
    params.set('sort', field)
    params.set('order', dir)
    const trimmedInput = inputSearch?.trim()
    if (trimmedInput) {
      params.set('input', trimmedInput)
    }
    const trimmedSubject = subject?.trim()
    if (trimmedSubject) {
      params.set('subject', trimmedSubject)
    }
    if (excludeUUIDs?.length) {
      params.set('excludeUUIDs', excludeUUIDs.join(','))
    }

    try {
      const templates = await restCall<INotificationTemplate[]>({
        method: 'get',
        url: `${NOTIFICATION_TEMPLATES_ENDPOINT}?${params.toString()}`,
      })

      return { Data: templates }
    } catch (e: unknown) {
      Logger.warn('Failed to read the notification template list', e)

      return { Data: [] }
    }
  }

  // Reads active wrapper templates from the onboarding REST endpoint
  // (GET /api/v1/onboarding/notification-templates/wrappers). Backs the
  // NotificationTemplatesWrappers CRUD provider (wrapper picker). Takes no
  // filter — the active + wrapper filters are applied server-side.
  public async fetchWrappers(
    paging: IPaging | IPagingFromTo,
    sort: IDataProviderSort | IDataProviderSort[] | undefined
  ) {
    const { page, pageSize } = paging as IPaging
    const { field, dir } = sort as IDataProviderSort

    // page is 0-based in the CRUD store; the endpoint expects 1-based.
    const params = new URLSearchParams()
    params.set('page', String(page + 1))
    params.set('itemsPerPage', String(pageSize))
    params.set('sort', field)
    params.set('order', dir)

    try {
      const wrappers = await restCall<INotificationTemplate[]>({
        method: 'get',
        url: `${NOTIFICATION_TEMPLATES_ENDPOINT}/wrappers?${params.toString()}`,
      })

      return { Data: wrappers }
    } catch (e: unknown) {
      Logger.warn('Failed to read the notification template wrappers', e)

      return { Data: [] }
    }
  }

  public async hideAllLocalesByUuid(uuid: string): Promise<boolean> {
    const wsClient = WebSocketStore.get('onboarding')
    const response = await wsClient.call<{
      isRemoved: boolean
      errorPayload?: IInvalidParamsErrorResponsePayload | undefined
    }>({
      service: WS_CONTROLLER,
      action: 'hideAllLocalesForTemplate',
      payload: { uuid },
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })
    if (response.status !== 'success') {
      const fallbackMessage = 'Failed'
      throwOnInvalidRequestParams(response, fallbackMessage)
      throwOnErrorWithoutPayload(response, fallbackMessage)
    }

    return response.data.isRemoved
  }

  public async hideOne(uuid: string): Promise<boolean> {
    const wsClient = WebSocketStore.get('onboarding')
    const response = await wsClient.call<{
      isRemoved: boolean
      errorPayload?: IInvalidParamsErrorResponsePayload | undefined
    }>({
      service: WS_CONTROLLER,
      action: 'hideOne',
      payload: { uuid },
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })
    if (response.status !== 'success') {
      const fallbackMessage = 'Failed'
      throwOnInvalidRequestParams(response, fallbackMessage)
      throwOnErrorWithoutPayload(response, fallbackMessage)
    }

    return response.data.isRemoved
  }

  public async checkCanApplyWrapper(
    wrapperUuid: string,
    targetUuid: string
  ): Promise<TCheckCanApplyWrapperResult> {
    const wsClient = WebSocketStore.get('onboarding')
    const response = await wsClient.call<{
      matchResult: TCheckCanApplyWrapperResult
      errorPayload?: IInvalidParamsErrorResponsePayload | undefined
    }>({
      service: WS_CONTROLLER,
      action: 'checkCanApplyWrapper',
      payload: { wrapperUuid, targetUuid },
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })
    if (response.status !== 'success') {
      const fallbackMessage = 'Failed'
      throwOnInvalidRequestParams(response, fallbackMessage)
      throwOnErrorWithoutPayload(response, fallbackMessage)
    }

    return response.data.matchResult
  }

  public async applyWrapperToLocales(
    wrapperUuid: string,
    targetUuid: string,
    options: TApplyWrapperOptions
  ): Promise<true> {
    const wsClient = WebSocketStore.get('onboarding')
    const response = await wsClient.call<{
      isApplied: true
      errorPayload?: IInvalidParamsErrorResponsePayload | undefined
    }>({
      service: WS_CONTROLLER,
      action: 'applyWrapperToLocales',
      payload: { wrapperUuid, targetUuid, options },
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })
    if (response.status !== 'success') {
      const fallbackMessage = 'Failed'
      throwOnInvalidRequestParams(response, fallbackMessage)
      throwOnErrorWithoutPayload(response, fallbackMessage)
    }

    return response.data.isApplied
  }

  public async renewNotificationInTransitionTemplate(payload: {
    transitionTemplateId: number
    notificationTemplateUUID: string
  }) {
    const wsClient = WebSocketStore.get('onboarding')
    let result

    try {
      result = await wsClient.call<{ renewed: boolean }>({
        service: WS_CONTROLLER,
        action: 'renewInTransitionTemplate',
        payload,
        withoutTokenRefresh: true,
        isAuth: true,
      })
    } catch (e: any) {
      const errorMessage = 'Failed to renewNotificationInTransitionTemplate'

      result = getErrorResponse(e.message || errorMessage, {
        renewed: null,
      })
    }

    return result
  }

  public async renewNotificationInActionTemplate(payload: {
    actionTemplateUUID: string
    notificationTemplateUUID: string
    notificationType: ActionNotifMode
  }) {
    const wsClient = WebSocketStore.get('onboarding')
    let result

    try {
      result = await wsClient.call<{ renewed: boolean }>({
        service: WS_CONTROLLER,
        action: 'renewInActionTemplate',
        payload,
        withoutTokenRefresh: true,
        isAuth: true,
      })
    } catch (e: any) {
      const errorMessage = 'Failed to renewNotificationInActionTemplate'
      result = getErrorResponse(e.message || errorMessage, {
        renewed: null,
      })
    }

    return result
  }

  public async renewNotificationInRequestTemplate(payload: {
    requestTemplateUUID: string
    notificationTemplateUUID: string
    notificationType: ERequestTemplateNotificationType
  }) {
    const wsClient = WebSocketStore.get('onboarding')
    let result

    try {
      result = await wsClient.call<{ renewed: boolean }>({
        service: WS_CONTROLLER,
        action: 'renewInRequestTemplate',
        payload,
        withoutTokenRefresh: true,
        isAuth: true,
      })
    } catch (e: any) {
      const errorMessage = 'Failed to renewNotificationInRequestTemplate'
      result = getErrorResponse(e.message || errorMessage, {
        renewed: null,
      })
    }

    return result
  }

  public async renewAllInheritedNotificationTemplates(notificationTemplateUUID: string) {
    const wsClient = WebSocketStore.get('onboarding')
    const payload = { notificationTemplateUUID }
    let result

    try {
      result = await wsClient.call<{ renewed: boolean }>({
        service: WS_CONTROLLER,
        action: 'renewAllInheritedNotificationTemplates',
        payload,
        withoutTokenRefresh: true,
        isAuth: true,
      })
    } catch (e: any) {
      const errorMessage = 'Failed to renewAllInheritedNotificationTemplates'
      result = getErrorResponse(e.message || errorMessage, {
        renewed: null,
      })
    }

    return result
  }
}

export const notificationTemplateApi = new NotificationTemplateApi()

export type TNotificationLocaleOption = { name: string; value: string }

export type TCheckCanApplyWrapperPartial = {
  status: 'partial'
  matches: TCheckCanApplyWrapperPartialMatches
}
export type TCheckCanApplyWrapperPartialMatches = {
  [locale: string]: { id: number; isMatched: boolean }
}

export type TCheckCanApplyWrapperResult =
  | {
      status: 'full' | 'none'
    }
  | TCheckCanApplyWrapperPartial

export type TApplyWrapperOptions = {
  mode: TApplyWrapperMode
  needReset?: boolean
}
export type TApplyWrapperMode = 'full' | 'partial'
